/**
 * WebsitePage — the two things that live on the customer's site: the tracking
 * Snippet and the WhatsApp button (widget). Pulled out of Integrations
 * (2026-06-09) so Integrations stays focused on account connections.
 *
 * Install mirrors onboarding: GTM gets the one-click card; every stack gets a
 * per-platform InstallGuide (collapsed behind a toggle); a live "Listening…"
 * pulse polls /me while waiting and stops once verified, then the whole install
 * collapses to "Your snippet is live ✓" with a View / re-install expander.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronDown, Code2, MessageCircle } from 'lucide-react';

import { apiRequest } from '@/lib/api';
import { useAuth } from '@/lib/useAuth';
import { buildSnippet } from '@/lib/snippet';
import { Checkbox } from '@/components/ui/checkbox';
import { PageHeader } from '@/components/PageHeader';
import { SnippetCard } from '@/components/SnippetCard';
import { WidgetCard } from '@/components/widget/WidgetCard';
import { GtmInstallCard } from '@/components/install/GtmInstallCard';
import { InstallGuide } from '@/components/install/InstallGuide';
import { SkeletonBox, SkeletonGroup } from '@/components/Skeleton';
import { ErrorState } from '@/components/ErrorState';

export function WebsitePage() {
  const { me, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [showGuide, setShowGuide] = useState(false);
  const [showInstall, setShowInstall] = useState(false);

  const snippetVerified = !!me?.snippet_verified_at;
  const noWebsiteChecked = !!me?.no_website_at;

  // Live verify: poll /me while waiting for the snippet to fire. Stops the
  // moment it's verified (or the user marked no-website) — "verified → stop
  // searching" (same rule as onboarding).
  useEffect(() => {
    if (snippetVerified || noWebsiteChecked) return;
    const id = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    }, 1500);
    return () => clearInterval(id);
  }, [snippetVerified, noWebsiteChecked, queryClient]);

  // Snippet "I don't have a website" skip — writes no_website_at on the client.
  const skipNoWebsite = useMutation({
    meta: { errorMessage: "Couldn't save that — try again" },
    mutationFn: () =>
      apiRequest('/onboarding/skip', {
        method: 'POST',
        body: { step: 'no_website' },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  const snippetCode = useMemo(() => {
    const clientId = me?.memberships?.[0]?.client_id;
    if (!clientId) return '';
    return buildSnippet(clientId, me?.site_tech_stack ?? null);
  }, [me?.memberships, me?.site_tech_stack]);

  if (authLoading) {
    return <WebsitePageSkeleton />;
  }
  if (!me) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
        <ErrorState title="Couldn't load your account" />
      </div>
    );
  }

  const clientId = me.memberships?.[0]?.client_id;
  const websiteUrl = me.website_url;
  const whatsappPaired = !!me.waba_id;
  const isGtm = me.site_tech_stack === 'gtm';
  const displaySite = websiteUrl?.replace(/^https?:\/\//, '') ?? 'your site';
  // Show the install (GTM card + snippet + guide)? Always while not verified;
  // behind the "View / re-install" toggle once verified.
  const installOpen = !snippetVerified || showInstall;

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <PageHeader
        title="Website"
        subtitle="The two things that live on your site: the tracking snippet and your WhatsApp button."
      />

      {/* GTM one-click — only when GTM is detected and the install panel is open
          (it collapses with the rest once verified). */}
      {isGtm && installOpen && (
        <GtmInstallCard
          websiteUrl={websiteUrl ?? null}
          snippetVerified={snippetVerified}
        />
      )}

      {/* Snippet + verify */}
      <section className="rounded-3xl border border-dark/5 bg-card p-6 shadow-card sm:p-8">
        <div className="mb-4 flex items-start gap-3">
          <Code2 className="mt-0.5 h-5 w-5 shrink-0 text-info" />
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-extrabold text-dark sm:text-xl">
              {isGtm && installOpen ? 'Or install manually' : 'Snippet'}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {noWebsiteChecked
                ? 'You marked yourself as not having a website. Attribution runs on contacts that match your connected ad platforms only.'
                : snippetVerified
                  ? 'The tracking snippet that powers your website attribution.'
                  : isGtm
                    ? 'Prefer to paste it yourself? Add this one line to every page of your site.'
                    : 'One line on every page of your site. Lets us match website visitors to their WhatsApp contacts.'}
            </p>
          </div>
        </div>

        {/* Live verify status — pulse while waiting, confirmed once it fires. */}
        {!noWebsiteChecked && (
          <div className="flex items-center justify-between gap-3 rounded-2xl bg-bg-tint p-4">
            {snippetVerified ? (
              <span className="flex items-center gap-2 text-sm text-dark">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-emerald-500" />
                <span>
                  <strong className="font-semibold">
                    Your snippet is live
                  </strong>{' '}
                  on {displaySite}.{' '}
                  <span className="text-emerald-600">✓</span>
                </span>
              </span>
            ) : (
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="relative flex h-2.5 w-2.5 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand/60" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand" />
                </span>
                Listening for your snippet…
              </span>
            )}
            {snippetVerified && (
              <button
                type="button"
                onClick={() => setShowInstall((s) => !s)}
                className="shrink-0 text-xs font-semibold text-info hover:underline"
              >
                {showInstall ? 'Hide' : 'View / re-install'}
              </button>
            )}
          </div>
        )}

        {/* Install — snippet + collapsible per-platform guide. */}
        {installOpen && !noWebsiteChecked && (
          <div className="mt-4 space-y-4">
            {snippetCode && (
              <SnippetCard
                mode="copy-button"
                label="The snippet"
                snippet={snippetCode}
              />
            )}
            {/* Per-platform steps — collapsed so they don't bury the widget. */}
            <div className="border-t border-dark/5 pt-4">
              <button
                type="button"
                onClick={() => setShowGuide((s) => !s)}
                aria-expanded={showGuide}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-info hover:underline"
              >
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${showGuide ? 'rotate-180' : ''}`}
                />
                {showGuide ? 'Hide install steps' : 'Step-by-step install guide'}
              </button>
              {showGuide && (
                <div className="mt-3">
                  <InstallGuide stack={me.site_tech_stack ?? 'unknown'} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* "I don't have a website" — only while not verified. */}
        {!snippetVerified && (
          <label className="mt-4 flex cursor-pointer items-start gap-3 border-t border-dark/5 pt-4">
            <Checkbox
              checked={noWebsiteChecked}
              disabled={skipNoWebsite.isPending}
              onCheckedChange={(c) => {
                if (c === true) skipNoWebsite.mutate();
              }}
              className="mt-0.5"
            />
            <span className="flex-1">
              <span className="block text-sm font-semibold text-dark">
                I don't have a website
              </span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                We won't ask you to install the snippet again. Attribution will
                only run on contacts that match your connected ad platforms.
              </span>
            </span>
          </label>
        )}
      </section>

      {/* WhatsApp button (widget) */}
      <div>
        <h2 className="mb-3 px-1 text-sm font-bold uppercase tracking-wider text-muted-foreground">
          WhatsApp button
        </h2>
        {whatsappPaired && clientId ? (
          <WidgetCard clientId={clientId} isFree={me.plan_tier === 'free'} />
        ) : (
          <section className="rounded-3xl border border-dark/5 bg-card p-6 shadow-card sm:p-8">
            <div className="flex items-start gap-3">
              <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
              <div>
                <h3 className="text-lg font-extrabold text-dark sm:text-xl">
                  Add a WhatsApp button to your site
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Connect WhatsApp first, then you can drop a floating WhatsApp
                  button on your site — customizable, and tracked automatically.
                </p>
                <Link
                  to="/integrations"
                  className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-info hover:underline"
                >
                  Connect WhatsApp in Integrations
                </Link>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function WebsitePageSkeleton() {
  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <SkeletonGroup className="space-y-6">
        <div>
          <SkeletonBox tone="prominent" className="h-10 w-44 sm:h-12" />
          <SkeletonBox className="mt-3 h-4 w-72 max-w-full" />
        </div>
        <SkeletonBox className="h-40 w-full rounded-3xl" />
        <SkeletonBox className="h-56 w-full rounded-3xl" />
      </SkeletonGroup>
    </div>
  );
}
